/*
 * ST 2026-09-28 walkthrough player (Wrike 4561036693) — markup in snippets/st-video-player.liquid.
 * Themed control bar around a Concept <video-media>, plus muted autoplay while at least half of the
 * player is visible and pause once it scrolls out. theme.js is untouched: the <video> is still
 * created by VideoMedia on the first play() (lazy), which also keeps LCP on the poster image.
 *
 * ST 2026-10-01 PDP gallery (Wrike 4577513707): when the <video-media> belongs to a data-video-group,
 * theme.js already plays/pauses it (exclusive group + active gallery slide). The player then skips its own
 * autoplay, attaches when VideoMedia starts the video, and routes play() through VideoMedia so the group
 * exclusivity still holds.
 */
(() => {
  if (customElements.get('st-video-player')) return;

  const SKIP_SECONDS = 10;
  // Default auto-hide delay; a player can set its own with data-idle-delay (ms), e.g. the PDP gallery (1500).
  const IDLE_DELAY = 2500;
  const INVIEW_RATIO = 0.5;

  const formatTime = (seconds) => {
    const total = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
  };

  class StVideoPlayer extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('video-media');
      if (!this.media || this.observer) return;
      // ST 2026-10-17 (Wrike 4585765122): the gallery morph (theme.js morphGalleryMedia) moves players on a variant
      // switch. The listeners below stay on the element; a second set would make one click toggle twice.
      if (this.initialized) {
        this.observe();
        return;
      }
      this.initialized = true;

      this.muted = true;
      this.userPaused = false;
      this.groupManaged = this.media.hasAttribute('data-video-group');
      this.scrubbing = false;
      this.idleDelay = Number(this.getAttribute('data-idle-delay')) || IDLE_DELAY;
      this.progress = this.querySelector('[data-progress]');
      this.elapsed = this.querySelector('[data-elapsed]');
      this.remaining = this.querySelector('[data-remaining]');
      this.setAttribute('data-muted', '');

      const connection = navigator.connection;
      const saveData = connection && (connection.saveData || /2g/.test(connection.effectiveType || ''));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayAllowed = !saveData && !reducedMotion;

      this.addEventListener('click', this.onClick.bind(this));
      // Touch: remember whether the controls were hidden before this tap wakes them, so onClick
      // only reveals them instead of pausing (wake() clears data-idle before the click fires).
      this.addEventListener('pointerdown', (event) => {
        this.tapReveals = event.pointerType !== 'mouse' && this.hasAttribute('data-idle');
        this.wake();
      }, { passive: true });
      this.addEventListener('pointercancel', () => {
        this.tapReveals = false;
      }, { passive: true });
      // PDP gallery: a gallery note (snippets/st-gallery-note.liquid) is a bar at the bottom of the slide, below the video;
      // keep the control bar above it.
      this.note = this.querySelector(':scope > .st-gallery-note');
      if (this.groupManaged) {
        this.wrapMediaPlay();
        this.media.addEventListener('video:play', () => {
          if (this.video) return;
          const video = this.media.querySelector('video');
          if (video) this.attach(video);
        });
      }
      ['pointermove', 'touchstart', 'focusin'].forEach((type) => {
        this.addEventListener(type, () => this.wake(), { passive: true });
      });
      // Keyboard focus leaving the player restarts the idle timer (it held the controls until now).
      // A blur because the controls were just hidden has no relatedTarget and is ignored.
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.wake();
      });

      this.progress?.addEventListener('input', () => {
        this.scrubbing = true;
        if (this.video && this.video.duration) {
          this.video.currentTime = (this.progress.value / 100) * this.video.duration;
        }
      });
      this.progress?.addEventListener('change', () => {
        this.scrubbing = false;
        this.wake();
      });

      const fullscreenButton = this.querySelector('[data-action="fullscreen"]');
      if (fullscreenButton && (this.requestFullscreen || this.webkitRequestFullscreen || 'webkitEnterFullscreen' in HTMLVideoElement.prototype)) {
        fullscreenButton.hidden = false;
      }

      // PDP gallery: a fullscreen slide leaves the slider's flex row (top layer, position: fixed), so the
      // slider scrolls on to the next slide meanwhile. On exit, scroll back to this slide.
      this.slider = this.parentElement?.closest('slider-element');
      if (this.slider) {
        this.onFullscreenChange = this.onFullscreenChange.bind(this);
        this.onNeighbourPlay = (event) => {
          if (event.target !== this.video && event.target instanceof HTMLVideoElement) event.target.pause();
        };
      }

      this.observe();
    }

    // Per connection: undone in disconnectedCallback.
    observe() {
      const note = this.note;
      if (note && 'ResizeObserver' in window) {
        this.noteObserver = new ResizeObserver(() => {
          this.style.setProperty('--st-video-player-note-height', `${note.offsetHeight}px`);
        });
        this.noteObserver.observe(note);
      }
      if (this.onFullscreenChange) {
        document.addEventListener('fullscreenchange', this.onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
      }

      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        if (entry.intersectionRatio >= INVIEW_RATIO) {
          if (this.autoplayAllowed && !this.userPaused && !this.groupManaged) this.play();
        }
        // Group videos (PDP gallery) are paused by theme.js once the gallery leaves the viewport (Wrike 4583328754).
        else if (!this.groupManaged && this.video && !this.video.paused) {
          this.video.pause();
          // ST 2026-10-25 (Wrike 4591208963): [data-load-visible] (PDPs) also stops a running download; play() resumes it.
          this.media.stopLoading?.();
        }
      }, { threshold: [0, INVIEW_RATIO] });
      this.observer.observe(this);
    }

    // The gallery restarts its active slide (MediaGallery.playActiveMedia) when fullscreen opens (desktop Safari
    // moves the slider before fullscreenchange fires), while it is fullscreen and right after the exit.
    // A user pause holds over all of these.
    wrapMediaPlay() {
      // Gallery markup parsed from a variant response (morphGalleryMedia) upgrades this element before the
      // <video-media> inside it, which then has no play() yet (Wrike 4585765122).
      if (typeof this.media.play !== 'function') customElements.upgrade(this.media);
      if (typeof this.media.play !== 'function') {
        customElements.whenDefined('video-media').then(() => {
          customElements.upgrade(this.media);
          if (typeof this.media.play === 'function') this.wrapMediaPlay();
        });
        return;
      }
      const mediaPlay = this.media.play.bind(this.media);
      this.media.play = () => {
        if (this.userPaused && (this.enteringFullscreen || this.inFullscreen || this.leavingFullscreen)) return;
        mediaPlay();
      };
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.noteObserver?.disconnect();
      this.noteObserver = null;
      clearTimeout(this.idleTimer);
      clearTimeout(this.restoreTimer);
      clearTimeout(this.enterTimer);
      clearTimeout(this.leaveTimer);
      if (this.onFullscreenChange) {
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
        this.slider.removeEventListener('play', this.onNeighbourPlay, true);
      }
    }

    onClick(event) {
      const button = event.target.closest('[data-action]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();

      const action = button.getAttribute('data-action');
      const tapReveals = this.tapReveals;
      this.tapReveals = false;
      // First tap on a video with hidden controls only reveals them (touch devices). The pointerdown already
      // made the buttons visible, so the click can land on the button under the finger (e.g. play/pause).
      if (tapReveals || (button.classList.contains('st-video-player__hit') && this.hasAttribute('data-idle'))) {
        this.wake();
        return;
      }

      if (action === 'toggle') this.toggle();
      if (action === 'back') this.skip(-SKIP_SECONDS);
      if (action === 'forward') this.skip(SKIP_SECONDS);
      if (action === 'mute') this.toggleMute();
      if (action === 'fullscreen') this.toggleFullscreen();
      if (action === 'pip') this.togglePictureInPicture();
      if (action === 'captions') this.toggleCaptions(button);
      this.wake();
    }

    play() {
      // Group videos: VideoMedia.play() pauses the other group members. It is a no-op while its
      // [playing] flag is still set (a pause during seeking keeps it), so the video is played directly below too.
      if (this.video && this.groupManaged && !this.media.playing) {
        this.video.muted = this.muted;
        this.media.play();
      }
      if (this.video) {
        const video = this.video;
        this.media.resumeLoading?.();
        video.muted = this.muted;
        video.play().catch((error) => {
          // iOS Safari blocks unmuted play() without a tap (e.g. scroll-back after the user unmuted):
          // continue muted, the mute button shows the state and one tap turns the sound back on.
          if (error.name === 'NotAllowedError' && !video.muted) {
            this.muted = true;
            video.muted = true;
            video.play().catch(() => {});
          }
        });
        return;
      }
      // VideoMedia builds the <video> from its template synchronously inside play().
      this.media.play();
      const video = this.media.querySelector('video');
      if (video) this.attach(video);
    }

    attach(video) {
      this.video = video;
      video.muted = this.muted;
      video.playsInline = true;
      // VideoMedia.suppressControls() disables PiP for all theme videos; this player offers it.
      video.disablePictureInPicture = false;

      video.addEventListener('play', () => {
        // Any start ends an earlier user pause, also when the gallery restarts the slide (playActiveMedia);
        // a stale flag would pause the video again on fullscreen exit.
        this.userPaused = false;
        this.media.paused = false;
        this.render();
      });
      video.addEventListener('pause', () => {
        // In fullscreen the slider moves on to the neighbour slide; if that is a video, the gallery plays it
        // and pauses this one. Only the user pauses a fullscreen video.
        const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
        if (fullscreenElement === this && !this.userPaused && !video.ended) {
          this.play();
          return;
        }
        this.render();
      });
      video.addEventListener('volumechange', () => {
        this.muted = video.muted;
        this.render();
      });
      video.addEventListener('timeupdate', () => this.renderTime());
      video.addEventListener('durationchange', () => this.renderTime());
      video.addEventListener('loadedmetadata', () => this.onMetadata(), { once: true });
      video.addEventListener('ended', () => {
        this.userPaused = true;
        this.render();
      });
      if (video.readyState >= 1) this.onMetadata();
      this.render();
    }

    onMetadata() {
      this.setAttribute('data-ready', '');
      const video = this.video;
      const pipButton = this.querySelector('[data-action="pip"]');
      if (pipButton && ((document.pictureInPictureEnabled && video.requestPictureInPicture) || video.webkitSupportsPresentationMode?.('picture-in-picture'))) {
        pipButton.hidden = false;
      }
      const captionsButton = this.querySelector('[data-action="captions"]');
      if (captionsButton && this.captionTrack()) captionsButton.hidden = false;
      this.renderTime();
    }

    toggle() {
      if (this.video && !this.video.paused) {
        this.userPaused = true;
        // DeferredMedia.paused stops the video group from resuming a video the user paused.
        this.media.paused = true;
        this.video.pause();
        return;
      }
      this.userPaused = false;
      this.media.paused = false;
      if (this.video?.ended) this.video.currentTime = 0;
      this.play();
    }

    skip(seconds) {
      const video = this.video;
      if (!video || !video.duration) return;
      video.currentTime = Math.min(Math.max(video.currentTime + seconds, 0), video.duration);
    }

    toggleMute() {
      this.muted = !this.muted;
      if (this.video) this.video.muted = this.muted;
      this.render();
    }

    toggleFullscreen() {
      const doc = document;
      if (doc.fullscreenElement || doc.webkitFullscreenElement) {
        (doc.exitFullscreen || doc.webkitExitFullscreen).call(doc);
        return;
      }
      // Cleared by fullscreenchange; the timeout covers a request that never opens.
      this.enteringFullscreen = true;
      clearTimeout(this.enterTimer);
      this.enterTimer = setTimeout(() => {
        this.enteringFullscreen = false;
      }, 1000);
      if (this.requestFullscreen) {
        this.requestFullscreen().catch(() => {});
      }
      else if (this.webkitRequestFullscreen) {
        this.webkitRequestFullscreen();
      }
      else {
        // iPhone Safari: only the <video> itself can go fullscreen (native iOS controls take over).
        if (!this.video) {
          this.userPaused = false;
          this.play();
        }
        this.video?.webkitEnterFullscreen?.();
      }
    }

    onFullscreenChange() {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      if (fullscreenElement === this) {
        this.inFullscreen = true;
        this.enteringFullscreen = false;
        // The neighbour slide the gallery moves to meanwhile must not play behind the fullscreen video.
        this.slider.addEventListener('play', this.onNeighbourPlay, true);
        return;
      }
      if (!this.inFullscreen || fullscreenElement) return;
      this.inFullscreen = false;
      // The gallery's replay after the exit waits for its resize settle (200 ms); slow devices need longer.
      this.leavingFullscreen = true;
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => {
        this.leavingFullscreen = false;
      }, 1000);
      this.slider.removeEventListener('play', this.onNeighbourPlay, true);
      // The gallery replays this slide after the exit, and that play clears userPaused; keep the state of now.
      const userPaused = this.userPaused;

      // MediaGallery.onWindowResize (theme.js) remembers the slide it saw at the first resize and
      // scrolls back to it 200 ms later; that is the neighbour by now, so point it at this slide.
      const gallery = this.closest('media-gallery');
      if (gallery) gallery.resizeActiveItem = this;

      const restore = () => {
        const first = this.slider.itemsToShow?.[0];
        if (!first || !this.isConnected || this.clientWidth === 0) return;
        const left = this.offsetLeft - first.offsetLeft;
        if (Math.abs(this.slider.scrollLeft - left) > 1) this.slider.scrollTo({ left, behavior: 'instant' });
      };
      requestAnimationFrame(restore);
      // Second pass after the gallery's resize settle (200 ms) and the slider:change that plays this slide.
      clearTimeout(this.restoreTimer);
      this.restoreTimer = setTimeout(() => {
        // Without a resize the gallery never consumes it; a stale value would pin a later resize to this slide.
        if (gallery?.resizeActiveItem === this) gallery.resizeActiveItem = null;
        restore();
        if (!this.video) return;
        if (userPaused) {
          this.userPaused = true;
          this.media.paused = true;
          if (!this.video.paused) this.video.pause();
        }
        // A neighbouring video slide played meanwhile and paused this one; resume it.
        else if (this.video.paused && this.slider.itemsToShow?.[this.slider.currentPage - 1] === this) {
          this.play();
        }
      }, 400);
    }

    togglePictureInPicture() {
      const video = this.video;
      if (!video) return;
      if (document.pictureInPictureEnabled && video.requestPictureInPicture) {
        if (document.pictureInPictureElement === video) {
          document.exitPictureInPicture().catch(() => {});
        }
        else {
          video.requestPictureInPicture().catch(() => {});
        }
      }
      else if (video.webkitSetPresentationMode) {
        video.webkitSetPresentationMode(video.webkitPresentationMode === 'picture-in-picture' ? 'inline' : 'picture-in-picture');
      }
    }

    captionTrack() {
      return Array.from(this.video?.textTracks || []).find((track) => track.kind === 'captions' || track.kind === 'subtitles');
    }

    toggleCaptions(button) {
      const track = this.captionTrack();
      if (!track) return;
      track.mode = track.mode === 'showing' ? 'hidden' : 'showing';
      button.setAttribute('aria-pressed', String(track.mode === 'showing'));
    }

    wake() {
      this.removeAttribute('data-idle');
      clearTimeout(this.idleTimer);
      if (this.video && !this.video.paused) {
        this.idleTimer = setTimeout(() => {
          // Only keyboard focus keeps the controls: a mouse click also focuses the button (Chrome),
          // which would otherwise stop the auto-hide until the focus moves elsewhere.
          if (this.video && !this.video.paused && !this.querySelector(':focus-visible') && !this.scrubbing) {
            this.setAttribute('data-idle', '');
          }
        }, this.idleDelay);
      }
    }

    render() {
      const playing = Boolean(this.video && !this.video.paused);
      const wasPlaying = this.getAttribute('data-state') === 'playing';
      this.setAttribute('data-state', playing ? 'playing' : 'paused');
      this.toggleAttribute('data-muted', this.muted);

      this.querySelectorAll('[data-action="toggle"][data-label-play]').forEach((button) => {
        button.setAttribute('aria-label', button.getAttribute(playing ? 'data-label-pause' : 'data-label-play'));
      });
      const muteButton = this.querySelector('[data-action="mute"]');
      muteButton?.setAttribute('aria-label', muteButton.getAttribute(this.muted ? 'data-label-unmute' : 'data-label-mute'));

      // Only the start of playback restarts the idle timer; repeated play/volumechange events would
      // otherwise keep the controls up longer than the idle delay.
      if (playing) {
        if (!wasPlaying) this.wake();
      }
      else {
        clearTimeout(this.idleTimer);
        this.removeAttribute('data-idle');
      }
    }

    renderTime() {
      const video = this.video;
      if (!video) return;
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      if (this.elapsed) this.elapsed.textContent = formatTime(video.currentTime);
      if (this.remaining) this.remaining.textContent = `-${formatTime(duration - video.currentTime)}`;
      if (this.progress && !this.scrubbing && duration) {
        this.progress.value = String((video.currentTime / duration) * 100);
        this.progress.setAttribute('aria-valuetext', `${formatTime(video.currentTime)} / ${formatTime(duration)}`);
      }
    }
  }

  customElements.define('st-video-player', StVideoPlayer);
})();
